import { useCallback, useMemo, useRef } from "react";
import { useSearchParams, type SetURLSearchParams } from "react-router-dom";
import { parseCommaSeparated } from "../utils/urlFilters.ts";

/** A null or empty value deletes the param; an array writes it repeated (`?a=1&a=2`). */
export type ParamUpdates = Record<string, string | string[] | null>;

export interface UrlState {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  updateParams: (updates: ParamUpdates, replace?: boolean) => void;
  /** Like updateParams, but also drops the reset key (page or cursor) and runs onFilterChange. */
  changeFilter: (updates: ParamUpdates, replace?: boolean) => void;
  /** Removes every URL param and runs onFilterChange. */
  clearAll: () => void;
}

interface UrlStateOptions {
  /** The pagination param a filter change resets. */
  resetKey?: "page" | "cursor";
  /** Extra in-memory state to reset when a filter changes (e.g. a cursor stack). */
  onFilterChange?: () => void;
}

/** List-page state kept in the URL query string. */
export function useUrlState({ resetKey = "page", onFilterChange }: UrlStateOptions = {}): UrlState {
  const [searchParams, setSearchParams] = useSearchParams();
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  const updateParams = useCallback(
    (updates: ParamUpdates, replace = false) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            next.delete(key);
            if (Array.isArray(value)) for (const v of value) next.append(key, v);
            else if (value !== null && value !== "") next.set(key, value);
          }
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  const changeFilter = useCallback(
    (updates: ParamUpdates, replace = false) => {
      updateParams({ ...updates, [resetKey]: null }, replace);
      onFilterChangeRef.current?.();
    },
    [updateParams, resetKey],
  );

  const clearAll = useCallback(() => {
    setSearchParams({});
    onFilterChangeRef.current?.();
  }, [setSearchParams]);

  return { searchParams, setSearchParams, updateParams, changeFilter, clearAll };
}

export type SortDir = "asc" | "desc";

/** `sort`/`dir` params; defaults are left out of the URL, and a new column starts at defaultDir. */
export function useUrlSort(state: UrlState, defaultCol: string, defaultDir: SortDir) {
  const sortBy = state.searchParams.get("sort") ?? defaultCol;
  const sortDir = (state.searchParams.get("dir") ?? defaultDir) as SortDir;

  const handleSort = (column: string) => {
    const newDir = sortBy === column ? (sortDir === "asc" ? "desc" : "asc") : defaultDir;
    state.changeFilter({
      sort: column === defaultCol ? null : column,
      dir: newDir === defaultDir ? null : newDir,
    });
  };

  return { sortBy, sortDir, handleSort };
}

/** Offset pagination in `page` (0-based) and `per_page`. */
export function useUrlPaging(state: UrlState, defaultPerPage = 50) {
  const page = parseInt(state.searchParams.get("page") ?? "0", 10) || 0;
  const rowsPerPage =
    parseInt(state.searchParams.get("per_page") ?? String(defaultPerPage), 10) || defaultPerPage;

  return {
    page,
    rowsPerPage,
    offset: page * rowsPerPage,
    setPage: (newPage: number) =>
      state.updateParams({ page: newPage === 0 ? null : String(newPage) }),
    setRowsPerPage: (n: number) =>
      state.updateParams({ per_page: n === defaultPerPage ? null : String(n), page: null }),
  };
}

/**
 * A comma-separated param as an array that keeps its identity until the
 * param's own value changes. A new array on an unrelated URL change would make
 * MUI Autocomplete reset the text typed into it.
 */
export function useCommaParam(searchParams: URLSearchParams, key: string): string[] {
  const raw = searchParams.get(key);
  return useMemo(() => parseCommaSeparated(raw), [raw]);
}
